import {
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Radio,
  RadioGroup,
  Separator,
} from '@fs/zion-ui'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserContext } from '../UserContext'
import { downloadUserReservationData } from '../../../services/userService'
import useNotifications from '../../../hooks/useNotifications'
import { downloadFile } from '../../../util/FileDownloadUtil'

const DOWNLOAD_STATE = { IDLE: 'idle', LOADING: 'loading', PARTIAL: 'partial' }

export default function UserReservationsDownloadDialog({ disableDownload, showOnlyCompletedOrdinances, ...opener }) {
  const { t } = useTranslation()
  const { currentUser } = useContext(UserContext)
  const [selected, setSelected] = useState('all')
  const [downloadState, setDownloadState] = useState(DOWNLOAD_STATE.IDLE)
  const [partialResult, setPartialResult] = useState(null) // { reservations: [], failedChunkIndexes: [] }
  const { error } = useNotifications()

  const downloadHeaders = [
    t('individualId', 'Individual ID'),
    t('spouse1.id', 'Spouse 1 ID'),
    t('spouse2.id', 'Spouse 2 ID'),
    t('ordinanceType', 'Ordinance Type'),
    t('owner', 'Owner'),
    t('reservation.shared', 'Shared'),
    t('reservedDate', 'Reserved Date'),
    t('reservations.assignedDate', 'Assigned Date'),
    t('reservations.printedDate', 'Printed Date'),
    t('reservations.expirationDate', 'Expiration Date'),
    t('ranId', 'RAN ID'),
  ]

  useEffect(() => {
    if (!opener.isOpen && !opener.transitioning) {
      setSelected('all')
      setDownloadState(DOWNLOAD_STATE.IDLE)
      setPartialResult(null)
    }
  }, [opener.isOpen, opener.transitioning])

  const handleChange = (e) => {
    setSelected(e.target.value)
  }

  const processReservationItems = (resItem) => {
    if (resItem.ordinanceType === 'SEALING_TO_SPOUSE') {
      resItem.currentIndividualId = resItem.currentSpouse1Id
    }
    const sharedType = t(`reservation.sharedType.${resItem.sharedType}`, resItem.sharedType) || ''
    const groupId = resItem.groupId ? ` - ${resItem.groupId}` : ''
    return [
      resItem.currentIndividualId || '',
      resItem.currentSpouse1Id || '',
      resItem.currentSpouse2Id || '',
      resItem.ordinanceType || '',
      resItem.ownerId || '',
      `${sharedType}${groupId}`,
      resItem.reservedDate || '',
      resItem.assignedDate || '',
      resItem.printedDate || '',
      resItem.expiryDate || '',
      resItem.ranId,
    ].join(',')
  }

  const buildAndDownloadCsv = (reservations, isPartial) => {
    const stringList = [downloadHeaders.join(','), ...reservations.map((resItem) => processReservationItems(resItem))]
    const filename = isPartial
      ? `${currentUser.contributorId} reservations (partial).csv`
      : `${currentUser.contributorId} reservations.csv`
    downloadFile(stringList.join('\n'), filename)
  }

  const handleFetchResult = (response, previousReservations) => {
    const { reservations, failedChunkIndexes } = response.data
    const mergedReservations = [...(previousReservations || []), ...(reservations || [])]

    if (!failedChunkIndexes || failedChunkIndexes.length === 0) {
      buildAndDownloadCsv(mergedReservations, false)
      opener.close()
      setDownloadState(DOWNLOAD_STATE.IDLE)
      setPartialResult(null)
    } else {
      setPartialResult({ reservations: mergedReservations, failedChunkIndexes })
      setDownloadState(DOWNLOAD_STATE.PARTIAL)
    }
  }

  const startDownload = (retryChunkIndexes, previousReservations) => {
    if (!disableDownload && currentUser.contributorId != null) {
      setDownloadState(DOWNLOAD_STATE.LOADING)
      downloadUserReservationData(currentUser.contributorId, showOnlyCompletedOrdinances, selected, retryChunkIndexes)
        .then((response) => {
          if (response.data.reservations === null && !retryChunkIndexes) {
            error(
              t('reservations.errors.noReservations', {
                defaultValue: 'No reservations found for selected type',
                message: 'No reservations found for selected type',
              })
            )
            setDownloadState(DOWNLOAD_STATE.IDLE)
            return
          }
          handleFetchResult(response, previousReservations)
        })
        .catch((err) => {
          error(t('reservations.errors.downloadUserReservationData', 'Error downloading user reservations'), err)
          setDownloadState(DOWNLOAD_STATE.IDLE)
        })
    }
  }

  const handleConfirm = () => startDownload(null, null)
  const handleRetry = () => startDownload(partialResult?.failedChunkIndexes, partialResult?.reservations)
  const handleDownloadPartial = () => {
    if (!partialResult) return
    buildAndDownloadCsv(partialResult.reservations, true)
    opener.close()
    setDownloadState(DOWNLOAD_STATE.IDLE)
    setPartialResult(null)
  }
  const handleCancel = () => {
    opener.close()
    setDownloadState(DOWNLOAD_STATE.IDLE)
    setPartialResult(null)
  }

  const renderFooter = () => {
    if (downloadState === DOWNLOAD_STATE.LOADING) {
      return (
        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button emphasis="medium" disabled>
              {t('reservations.download.loading', 'Downloading…')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      )
    }
    if (downloadState === DOWNLOAD_STATE.PARTIAL && partialResult) {
      const failedCount = partialResult.failedChunkIndexes.length
      return (
        <DialogOverlayFooter>
          <p>
            {t(
              'reservations.download.partial',
              `Download incomplete: ${failedCount} chunk(s) failed — some reservations are missing.`
            )}
          </p>
          <Separator />
          <DialogOverlayActions>
            <Button emphasis="high" onClick={handleRetry}>
              {t('reservations.download.retryFailed', 'Retry failed chunks')}
            </Button>
            <Button emphasis="medium" onClick={handleDownloadPartial}>
              {t('reservations.download.downloadPartial', 'Download partial results')}
            </Button>
          </DialogOverlayActions>
          <DialogOverlayActions>
            <Button emphasis="medium" onClick={handleCancel}>
              {t('action.cancel', 'Cancel')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      )
    }
    return (
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={handleConfirm}>
            {t('action.confirm', 'Confirm')}
          </Button>
          <Button emphasis="medium" onClick={opener.close}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    )
  }

  return (
    <DialogOverlay
      {...opener}
      title={t('user.buttons.downloadReservations.description', 'Download Reservations')}
      subtitle={currentUser.dmcUser?.cisId?.split('.')[2]}
    >
      {downloadState === DOWNLOAD_STATE.IDLE && (
        <RadioGroup>
          <Radio
            checked={selected === 'all'}
            label={t('user.buttons.allReservations', 'All Reservations')}
            name={t('user.buttons.allReservations', 'All Reservations')}
            value="all"
            onChange={handleChange}
          />
          <Radio
            checked={selected === 'individual'}
            label={t('user.buttons.individual', 'Individual')}
            name={t('user.buttons.individual', 'Individual')}
            value="individual"
            onChange={handleChange}
          />
          <Radio
            checked={selected === 'group'}
            label={t('user.buttons.sharedWithGroup', 'Shared With Group')}
            name={t('user.buttons.sharedWithGroup', 'Shared With Group')}
            value="group"
            onChange={handleChange}
          />
          <Radio
            checked={selected === 'temple'}
            label={t('user.buttons.sharedWithTemple', 'Shared With Temple')}
            name={t('user.buttons.sharedWithTemple', 'Shared With Temple')}
            value="temple"
            onChange={handleChange}
          />
        </RadioGroup>
      )}
      {renderFooter()}
    </DialogOverlay>
  )
}
